import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { memo, useState, useEffect } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import { AppSelect, colorTokens } from '@eruvaka/web-pondlogs-base';
import { fetchPreferences, updatePreferences, resetPreferences } from '../../api/preferencesApi';
import { useTranslation } from '@eruvaka/web-pondlogs-i18n';
import { ValidationSection } from './ValidationSection';
import { AdvancedRulesSection } from './AdvancedRulesSection';
// ── Default validation ranges (mirrors BFF defaults) ────────────────────────────────
const DEFAULTS = {
    waterQuality: [
        { parameter: 'pH', min: 5.5, max: 10, unit: '' },
        { parameter: 'Temperature', min: 15, max: 40, unit: '°C' },
        { parameter: 'DO', min: 0, max: 20, unit: 'mg/l' },
        { parameter: 'Salinity', min: 0, max: 45, unit: 'ppt' },
        { parameter: 'Alkalinity', min: 5, max: 600, unit: 'mg/l' },
        { parameter: 'Hardness', min: 10, max: 10000, unit: 'ppm' },
        { parameter: 'TAN / NH4', min: 0, max: 10, unit: 'ppm' },
    ],
    chemicalMinerals: [
        { parameter: 'Ca', min: 0, max: 1000, unit: 'ppm' },
        { parameter: 'Mg', min: 0, max: 1000, unit: 'ppm' },
        { parameter: 'K / Kali', min: 0, max: 1000, unit: 'ppm' },
        { parameter: 'Fe', min: 0, max: 5, unit: 'ppm' },
        { parameter: 'NO2', min: 0, max: 100, unit: 'ppm' },
    ],
    organicLoad: [
        { parameter: 'NTU', min: 0, max: 4000, unit: '' },
        { parameter: 'Secchi Disk', min: 0, max: 100, unit: 'cm' },
    ],
    microbiology: [
        { parameter: 'Vibrio', min: 0, max: 10, unit: 'CFU/ml' },
        { parameter: 'V. Vulnificus / Cholerae', min: 0, max: 10, unit: '' },
        { parameter: 'V. Parahaemolyticus', min: 0, max: 10, unit: '' },
        { parameter: 'V. Alginolyticus', min: 0, max: 10, unit: '' },
        { parameter: 'Total Bacteria Count', min: 0, max: 10, unit: '' },
        { parameter: 'Lumines', min: 0, max: 10, unit: '' },
    ],
    planktonAlgae: [
        { parameter: 'Green Algae', min: 0, max: 10, unit: '' },
        { parameter: 'Blue Green Algae', min: 0, max: 10, unit: '' },
        { parameter: 'Diatom', min: 0, max: 10, unit: '' },
        { parameter: 'Dinoflagellate (Toxic)', min: 0, max: 10, unit: '' },
    ],
    shrimpHealth: [
        { parameter: 'Green Colonies (Shrimp/Water)', min: 0, max: 10000, unit: '' },
        { parameter: 'Yellow Colonies (Shrimp/Water)', min: 0, max: 10000, unit: '' },
    ],
    operations: [
        { parameter: 'Feed Quantity', min: 0, max: 500, unit: 'kg' },
        { parameter: 'ABW', min: 0, max: 50, unit: 'g' },
        { parameter: 'AWG', min: -2, max: 10, unit: 'g' },
        { parameter: 'Medicine Quantity', min: 0, max: 1000, unit: '' },
    ],
};
export const Preferences = memo(() => {
    const { t } = useTranslation('main');
    const [prefs, setPrefs] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [saved, setSaved] = useState(false);
    const [error, setError] = useState(null);
    const [selected, setSelected] = useState('display');
    const [dragFrom, setDragFrom] = useState(null);
    useEffect(() => {
        fetchPreferences()
            .then(setPrefs)
            .catch(() => setError('errors.loadPreferencesFailed'))
            .finally(() => setLoading(false));
    }, []);
    const setDisplay = (patch) => setPrefs((p) => (p ? { ...p, display: { ...p.display, ...patch } } : p));
    const setUnits = (patch) => setPrefs((p) => (p ? { ...p, units: { ...p.units, ...patch } } : p));
    const setValidationRange = (group, parameter, field, value) => setPrefs((p) => {
        if (!p)
            return p;
        const updated = p.validation[group].map((r) => r.parameter === parameter ? { ...r, [field]: value } : r);
        return { ...p, validation: { ...p.validation, [group]: updated } };
    });
    const resetGroup = async (group) => {
        try {
            const reset = await resetPreferences('validation');
            setPrefs((p) => p ? { ...p, validation: { ...p.validation, [group]: reset.validation[group] } } : p);
        }
        catch {
            // reset to local defaults
            setPrefs((p) => (p ? { ...p, validation: { ...p.validation, [group]: DEFAULTS[group] } } : p));
        }
    };
    const handleSave = async () => {
        if (!prefs)
            return;
        setSaving(true);
        setError(null);
        try {
            const updated = await updatePreferences({
                display: prefs.display,
                units: prefs.units,
                validation: prefs.validation,
            });
            setPrefs(updated);
            setSaved(true);
            setTimeout(() => setSaved(false), 2500);
        }
        catch (e) {
            setError('errors.savePreferencesFailed');
        }
        finally {
            setSaving(false);
        }
    };
    if (loading)
        return (_jsx(Box, { sx: { display: 'flex', justifyContent: 'center', py: 4 }, children: _jsx(CircularProgress, {}) }));
    if (!prefs)
        return _jsx(Alert, { severity: "error", children: t('preferences.loadError') });
    const { display, units, validation } = prefs;
    const valGroups = [
        {
            key: 'waterQuality',
            label: t('preferences.waterQuality'),
            sub: `${validation.waterQuality.length} parameters`,
        },
        {
            key: 'chemicalMinerals',
            label: t('preferences.chemicalsMinerals'),
            sub: `${validation.chemicalMinerals.length} parameters`,
        },
        {
            key: 'organicLoad',
            label: t('preferences.organicLoad'),
            sub: `${validation.organicLoad.length} parameters`,
        },
        {
            key: 'microbiology',
            label: t('preferences.microbiology'),
            sub: `${validation.microbiology.length} parameters`,
        },
        {
            key: 'planktonAlgae',
            label: t('preferences.planktonAlgae'),
            sub: `${validation.planktonAlgae.length} parameters`,
        },
        {
            key: 'shrimpHealth',
            label: t('preferences.shrimpHealth'),
            sub: `${validation.shrimpHealth.length} parameters`,
        },
        {
            key: 'operations',
            label: t('preferences.operations'),
            sub: `${validation.operations.length} parameters`,
        },
    ];
    const sidebarCategories = [
        {
            id: 'display',
            label: t('settings.sectionHeaders.displayGeneral'),
            sub: t('preferences.displaySectionSub'),
        },
        {
            id: 'units',
            label: t('settings.sectionHeaders.unitsFormatting'),
            sub: t('preferences.unitsSectionSub'),
        },
        ...valGroups.map((g) => ({ id: g.key, label: g.label, sub: g.sub })),
        {
            id: 'rules',
            label: t('settings.sectionHeaders.advancedMonitoringRules'),
            sub: t('preferences.advancedRulesSectionSub'),
        },
    ];
    const activeCategory = sidebarCategories.find((c) => c.id === selected) ?? sidebarCategories[0];
    const isValidationGroup = valGroups.some((g) => g.key === selected);
    const contentSub = isValidationGroup
        ? `${activeCategory.sub} \u2014 configure min/max validation ranges.`
        : activeCategory.sub;
    const unitRows = [
        {
            label: 'Date Format',
            key: 'dateFormat',
            opts: ['DD MMM YY', 'DD MMM', 'DD/MM/YYYY'],
        },
        {
            label: 'Time Format',
            key: 'timeFormat',
            opts: ['HH:MM', 'HH:MM AM/PM'],
        },
        { label: 'Biomass', key: 'biomassUnit', opts: ['kg', 'lb'] },
        { label: 'Pond Area', key: 'pondAreaUnit', opts: ['ac', 'ha'] },
        {
            label: 'Biomass / Area',
            key: 'biomassPerAreaUnit',
            opts: ['kg/ac', 'kg/ha', 'lb/ac', 'lb/ha'],
        },
    ];
    return (_jsxs(Box, { sx: {
            display: 'flex',
            flexDirection: 'column',
            gap: 1.5,
            height: '100%',
            overflow: 'hidden',
        }, children: [error && (_jsx(Alert, { severity: "error", onClose: () => setError(null), children: t(error) })), _jsxs(Box, { sx: {
                    display: 'flex',
                    gap: 1.5,
                    alignItems: 'flex-start',
                    flex: 1,
                    minHeight: 0,
                    overflow: 'hidden',
                }, children: [_jsxs(Paper, { variant: "outlined", sx: {
                            width: 224,
                            flexShrink: 0,
                            borderRadius: 2,
                            overflow: 'hidden',
                            position: 'sticky',
                            top: 0,
                            maxHeight: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                        }, children: [_jsx(Box, { sx: { px: 1.5, py: 1.25, borderBottom: `1px solid ${colorTokens.borderLight}` }, children: _jsx(Typography, { sx: {
                                        fontSize: 9,
                                        fontWeight: 700,
                                        letterSpacing: '0.08em',
                                        color: colorTokens.darkGrey,
                                        textTransform: 'uppercase',
                                    }, children: "Categories" }) }), _jsx(Box, { sx: { overflowY: 'auto', flex: 1, pb: 1 }, children: sidebarCategories.map((cat, idx) => {
                                    const isSelected = selected === cat.id;
                                    return (_jsxs(Box, { onClick: () => setSelected(cat.id), sx: {
                                            px: 1.5,
                                            py: 1.25,
                                            cursor: 'pointer',
                                            borderLeft: isSelected
                                                ? `3px solid ${colorTokens.magenta}`
                                                : '3px solid transparent',
                                            bgcolor: isSelected ? `${colorTokens.magenta}0d` : 'transparent',
                                            borderBottom: idx < sidebarCategories.length - 1
                                                ? `1px solid ${colorTokens.borderLight}`
                                                : 'none',
                                            '&:hover': { bgcolor: isSelected ? `${colorTokens.magenta}0d` : '#f8fafc' },
                                            transition: 'background 0.12s',
                                        }, children: [_jsx(Typography, { sx: {
                                                    fontSize: 12,
                                                    fontWeight: isSelected ? 700 : 500,
                                                    color: isSelected ? colorTokens.primary : '#1a2233',
                                                }, children: cat.label }), _jsx(Typography, { sx: { fontSize: 10, color: colorTokens.darkGrey, mt: 0.25 }, children: cat.sub })] }, cat.id));
                                }) })] }), _jsxs(Paper, { variant: "outlined", sx: {
                            flex: 1,
                            borderRadius: 2,
                            overflow: 'hidden',
                            minWidth: 0,
                            display: 'flex',
                            flexDirection: 'column',
                            minHeight: 0,
                        }, children: [_jsxs(Box, { sx: { px: 2.5, py: 2, borderBottom: `1px solid ${colorTokens.borderLight}` }, children: [_jsx(Typography, { sx: { fontSize: 15, fontWeight: 700, color: '#1a2233' }, children: activeCategory.label }), _jsx(Typography, { sx: { fontSize: 12, color: colorTokens.primary, mt: 0.5 }, children: contentSub })] }), _jsxs(Box, { sx: { p: 2.5, flex: 1, overflowY: 'auto', minHeight: 0 }, children: [selected === 'display' && (_jsxs(Stack, { spacing: 2.5, children: [_jsxs(Box, { sx: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 2 }, children: [_jsxs(Box, { children: [_jsx(Typography, { sx: { fontSize: 11, fontWeight: 700, color: colorTokens.primary, mb: 0.75 }, children: t('preferences.deviceNameDisplay') }), _jsx(AppSelect, { options: [
                                                                    { value: 'DEVICE_ID', label: t('preferences.deviceNameDisplayDeviceId') },
                                                                    { value: 'ALIAS_NAME', label: t('preferences.deviceNameDisplayAliasName') },
                                                                ], value: display.deviceNameDisplay, onChange: (v) => setDisplay({ deviceNameDisplay: v }) })] }), _jsxs(Box, { children: [_jsx(Typography, { sx: { fontSize: 11, fontWeight: 700, color: colorTokens.primary, mb: 0.75 }, children: t('preferences.feedGap') }), _jsx(AppSelect, { options: [
                                                                    { value: 'SHOW', label: t('preferences.feedGapShow') },
                                                                    { value: 'HIDE', label: t('preferences.feedGapHide') },
                                                                ], value: display.feedGapVisibility, onChange: (v) => setDisplay({ feedGapVisibility: v }) })] })] }), _jsxs(Box, { children: [_jsx(Typography, { sx: { fontSize: 11, fontWeight: 700, color: colorTokens.primary, mb: 0.5 }, children: t('preferences.alertPriorityOrder') }), _jsx(Typography, { sx: { fontSize: 10, color: colorTokens.darkGrey, mb: 1 }, children: t('preferences.alertPriorityDragHint') }), _jsx(Stack, { spacing: 0.75, sx: { maxWidth: 360 }, children: display.alertPriority.map((item, idx) => (_jsxs(Box, { draggable: true, onDragStart: () => setDragFrom(idx), onDragOver: (e) => e.preventDefault(), onDrop: () => {
                                                                if (dragFrom === null || dragFrom === idx)
                                                                    return;
                                                                const next = [...display.alertPriority];
                                                                const [moved] = next.splice(dragFrom, 1);
                                                                next.splice(idx, 0, moved);
                                                                setDisplay({ alertPriority: next });
                                                                setDragFrom(null);
                                                            }, onDragEnd: () => setDragFrom(null), sx: {
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                gap: 1.5,
                                                                px: 1.5,
                                                                py: 1,
                                                                borderRadius: 2,
                                                                border: '1px solid',
                                                                borderColor: dragFrom === idx ? colorTokens.primary : colorTokens.borderLight,
                                                                background: dragFrom === idx ? `${colorTokens.primary}08` : 'white',
                                                                cursor: 'grab',
                                                                userSelect: 'none',
                                                                '&:active': { cursor: 'grabbing' },
                                                            }, children: [_jsx(Box, { sx: {
                                                                        display: 'flex',
                                                                        flexDirection: 'column',
                                                                        gap: '2px',
                                                                        flexShrink: 0,
                                                                    }, children: [0, 1, 2].map((i) => (_jsx(Box, { sx: {
                                                                            width: 14,
                                                                            height: 1.5,
                                                                            background: colorTokens.darkGrey,
                                                                            borderRadius: 0.5,
                                                                            opacity: 0.5,
                                                                        } }, i))) }), _jsx(Box, { sx: {
                                                                        width: 20,
                                                                        height: 20,
                                                                        borderRadius: '50%',
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        justifyContent: 'center',
                                                                        background: `${colorTokens.primary}15`,
                                                                        color: colorTokens.primary,
                                                                        fontSize: 10,
                                                                        fontWeight: 700,
                                                                        flexShrink: 0,
                                                                    }, children: idx + 1 }), _jsx(Typography, { sx: { fontSize: 12, fontWeight: 600, color: '#1a2233' }, children: item })] }, item))) })] })] })), selected === 'units' && (_jsx(Box, { sx: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 2 }, children: unitRows.map(({ label, key, opts }) => (_jsxs(Box, { children: [_jsx(Typography, { sx: { fontSize: 11, fontWeight: 700, color: colorTokens.primary, mb: 0.75 }, children: label }), _jsx(AppSelect, { options: opts.map((o) => ({ value: o, label: o })), value: units[key], onChange: (v) => setUnits({ [key]: v }) })] }, key))) })), isValidationGroup && (_jsx(ValidationSection, { parameters: validation[selected], defaults: DEFAULTS[selected], onChange: (parameter, field, value) => setValidationRange(selected, parameter, field, value), onResetAll: () => resetGroup(selected) })), selected === 'rules' && _jsx(AdvancedRulesSection, {})] }), _jsxs(Box, { sx: {
                                    display: 'flex',
                                    justifyContent: 'flex-end',
                                    gap: 1,
                                    px: 2.5,
                                    py: 1.5,
                                    borderTop: `1px solid ${colorTokens.borderLight}`,
                                }, children: [_jsx(Button, { variant: "outlined", size: "small", onClick: () => { }, sx: {
                                            borderColor: colorTokens.borderLight,
                                            color: colorTokens.darkGrey,
                                            textTransform: 'none',
                                            fontWeight: 600,
                                            fontSize: 12,
                                            borderRadius: '12px',
                                            '&:hover': { borderColor: colorTokens.borderLight, background: 'rgba(0,0,0,0.04)' },
                                        }, children: t('preferences.cancel') }), _jsx(Button, { variant: "contained", color: "primary", size: "medium", onClick: handleSave, disabled: saving, startIcon: saving ? _jsx(CircularProgress, { size: 12, color: "inherit" }) : undefined, sx: {
                                            minHeight: 36,
                                            minWidth: 160,
                                            borderRadius: '12px',
                                            textTransform: 'none',
                                            fontWeight: 700,
                                            fontSize: 12,
                                            px: 2.5,
                                            ...(saved
                                                ? { background: colorTokens.green, '&:hover': { background: colorTokens.green } }
                                                : {}),
                                        }, children: saved ? t('preferences.saved') : t('preferences.savePreferences') })] })] })] })] }));
});
Preferences.displayName = 'Preferences';
